import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@monecolier/ui';
import { getMe, type MembershipRole } from '@monecolier/api-client';
import { canAccessRoute, homePathForRoles, requiredRolesForPath } from '../lib/roles';

/** État de navigation posé par la redirection, lu par `AccessRedirectNotice`. */
export interface AccessRedirectState {
  accessRedirect: true;
}

/**
 * UX-79 — garde de route au niveau du ROUTEUR, montée UNE fois autour de
 * `<Routes>` (App.tsx) : aucune route restreinte ne peut être oubliée, la
 * décision vient de `ROUTE_ACCESS` et de `location.pathname`. Un compte dont aucun rôle ne
 * figure dans `ROUTE_ACCESS` (lib/roles.ts) pour la route courante est
 * redirigé IMMÉDIATEMENT vers son accueil, avec un état de navigation qui fait
 * afficher `AccessRedirectNotice`. Couvre aussi les liens profonds (email,
 * favori) : la garde s'exécute avant que la page ne soit montée.
 *
 * Laisse volontairement la page décider dans trois cas, jamais redirigés :
 * - compte non connecté (la page renvoie vers `/login`) ;
 * - compte sans Membership (`roles` vide : la page affiche `AccessDenied`,
 *   écran de création d'établissement/révocation — hors périmètre UX-79) ;
 * - échec de `GET /me` (le backend reste la vraie barrière, la page affiche
 *   son erreur habituelle plutôt qu'une redirection sur une supposition).
 *
 * Les rôles sont conservés entre deux routes et la décision est recalculée à
 * CHAQUE rendu depuis `location.pathname` : React réutilise cette instance
 * quand on passe d'une route gardée à une autre, un état "autorisé" figé
 * laisserait passer la seconde.
 */
export function RequireRouteAccess({ children }: { children: ReactNode }) {
  const { accessToken, apiClient, isAuthenticated } = useAuth();
  const location = useLocation();
  // `undefined` = pas encore résolu ; `null` = résolution impossible (laisse passer).
  const [roles, setRoles] = useState<MembershipRole[] | null | undefined>(undefined);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return;
    let cancelled = false;
    getMe(apiClient, accessToken)
      .then((me) => {
        if (!cancelled) setRoles(me.roles ?? []);
      })
      .catch(() => {
        if (!cancelled) setRoles(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accessToken, apiClient]);

  // Route ouverte (absente de `ROUTE_ACCESS`) : rendue sans attendre `GET /me`.
  if (requiredRolesForPath(location.pathname) === null) return <>{children}</>;
  if (!isAuthenticated || !accessToken || roles === null) return <>{children}</>;
  // Rien n'est rendu tant que les rôles ne sont pas connus : monter la page
  // déclencherait ses appels réseau et un flash d'erreur 403 avant la redirection.
  if (roles === undefined) return null;
  if (roles.length === 0 || canAccessRoute(roles, location.pathname)) return <>{children}</>;

  const state: AccessRedirectState = { accessRedirect: true };
  return <Navigate to={homePathForRoles(roles)} replace state={state} />;
}
